import { isAxiosError } from 'axios';
import { Result, err, ok } from 'neverthrow';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore utils is in the parent dir
import axios from '@utils/axios';

import { ApiError } from 'types/api/ApiError';
import captureException from 'utils/captureException';

// Matches `PortalCustomerIDScope` JSON from GET /integrations/credentials/options.
export interface PortalCustomerIdScope {
  scopeUint?: number;
  scopeText?: string;
}

/**
 * One row of {@link CredentialOptionsResponse.eligibleCredentials}.
 *
 * Backend: `GET /integrations/credentials/options` returns
 * `GetCredentialOptionsResponse` with `EligibleCredentials []credentials.PortalCredentialOption`
 * (see `common/integrations` handler). Keep this aligned with `PortalCredentialOption` JSON tags.
 */
export interface EligibleCredential {
  credentialId: number;
  scopeBucket: string;
  level: string;
  ownerId?: number;
  integrationType: string;
  integrationName: string;
  authType: string;
  priority: number;
  usageCount: number;
  lastUsedAt: string;
  customerIdScopes: PortalCustomerIdScope[];
  // Username for display in the login picker (from GET /integrations/credentials/options).
  username?: string;
}

/**
 * Mirrors integrations `GetCredentialOptionsResponse` JSON shape.
 * `integrationId` is `integrations.id` for this portal request.
 */
export interface CredentialOptionsResponse {
  eligibleCredentials: EligibleCredential[];
  defaultCredentialId: number;
  recommendationReasons: string[];
  integrationId?: number;
}

// Fallback when credential options could not be loaded (e.g. API error).
export const EMPTY_CREDENTIAL_OPTIONS_RESPONSE: CredentialOptionsResponse = {
  eligibleCredentials: [],
  defaultCredentialId: 0,
  recommendationReasons: [],
};

export interface GetCredentialOptionsParams {
  integrationName: string;
  tmsCustomerId?: string;
  customerName?: string;
}

/**
 * Query keys must match `GetCredentialOptionsQuery` in drumkit
 * `fn/api/routes/integrations/get_credential_options.go` (integrationName, tmsCustomerId, customerName).
 */
export async function getCredentialOptions(
  params: GetCredentialOptionsParams
): Promise<Result<CredentialOptionsResponse, ApiError>> {
  try {
    const requestParams: Record<string, string> = {
      integrationName: params.integrationName,
    };

    const tms = params.tmsCustomerId?.trim();

    if (tms) {
      requestParams.tmsCustomerId = tms;
    }

    const customerName = params.customerName?.trim();

    if (customerName) {
      requestParams.customerName = customerName;
    }

    const { data } = await axios.get<CredentialOptionsResponse>(
      '/integrations/credentials/options',
      {
        params: requestParams,
      }
    );

    return ok(data);
  } catch (error) {
    captureException(error, { functionName: 'getCredentialOptions' });

    if (!isAxiosError(error)) {
      return err({ message: 'Failed to fetch credential options' });
    }

    const data = error.response?.data;

    let message = 'Failed to fetch credential options';

    if (typeof data === 'string') {
      message = data;
    } else if (
      typeof data === 'object' &&
      data !== null &&
      'message' in data &&
      typeof (data as { message: unknown }).message === 'string'
    ) {
      message = (data as { message: string }).message;
    }

    return err({
      message,
      status: error.response?.status,
    });
  }
}
